import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PlatformShell from '../../components/PlatformShell';
import { DataTable } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';

export default function PlatformFleetAudit() {
  const [hospitalFilter, setHospitalFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const { data: hospData } = useLiveData(() => ipApi.platformHospitals({ includeArchived: true }), []);
  const hospitals = hospData?.hospitals || [];
  const { data, loading, error, reload } = useLiveData(
    () => ipApi.platformFleetAudit({ limit: 300, hospitalId: hospitalFilter || undefined }),
    [hospitalFilter],
  );
  const events = data?.events || [];

  const filtered = useMemo(() => {
    if (!actionFilter.trim()) return events;
    const q = actionFilter.toLowerCase();
    return events.filter((e) => (e.action || '').toLowerCase().includes(q));
  }, [events, actionFilter]);

  const columns = [
    {
      key: 'at',
      label: 'When',
      render: (r) => (r.createdAt || '').replace('T', ' ').slice(0, 19),
    },
    {
      key: 'action',
      label: 'Action',
      render: (r) => <code>{r.action || '—'}</code>,
    },
    {
      key: 'hospital',
      label: 'Hospital',
      render: (r) => r.hospitalId ? (
        <Link to={`/platform/hospitals/${r.hospitalId}?tab=audit`}>{r.hospitalId}</Link>
      ) : '—',
    },
    { key: 'email', label: 'Email', render: (r) => r.email || '—' },
    { key: 'by', label: 'By', render: (r) => r.by || r.byName || '—' },
    {
      key: 'detail',
      label: 'Detail',
      render: (r) => {
        const skip = new Set(['action', 'createdAt', 'hospitalId', 'email', 'by', 'byName', 'id']);
        const parts = Object.entries(r).filter(([k, v]) => !skip.has(k) && v != null && v !== '');
        return parts.slice(0, 4).map(([k, v]) => `${k}: ${String(v)}`).join(' · ') || '—';
      },
    },
  ];

  return (
    <PlatformShell title="Fleet audit log" subtitle="Cross-hospital activity — last 300 events">
      {error && <div className="error mb">{error}</div>}

      <div className="card mb platform-toolbar">
        <div className="platform-filters">
          <select className="input" value={hospitalFilter} onChange={(e) => setHospitalFilter(e.target.value)}>
            <option value="">All hospitals</option>
            {hospitals.map((h) => (
              <option key={h.hospitalId} value={h.hospitalId}>{h.name} ({h.hospitalId})</option>
            ))}
          </select>
          <input
            className="input"
            placeholder="Filter by action (e.g. hospital_onboard, staff_login)"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => reload()}>Refresh</button>
        </div>
      </div>

      <div className="card">
        <h3>Audit events {loading ? '(loading…)' : `(${filtered.length})`}</h3>
        <p className="hint mb">Includes hospital onboarding, staff changes, owner password resets, OP linking, and platform admin actions.</p>
        <DataTable columns={columns} rows={filtered} keyField={(r) => `${r.createdAt}-${r.action}-${r.hospitalId || ''}`} emptyMessage="No audit events yet." />
      </div>
    </PlatformShell>
  );
}
